import { useState } from 'react';
import { createNewLoan, importMISMOLoan, validateLoanData, LOAN_TYPES, LOAN_PURPOSES } from '../../services/loanService';

const NewLoanModal = ({ show, onClose, onLoanCreated }) => {
  const [step, setStep] = useState('choice'); // 'choice', 'native', 'import'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Native form state
  const [formData, setFormData] = useState({
    loanPurpose: '',
    loanType: '',
    borrowerFirstName: '',
    borrowerLastName: '',
    propertyState: '',
    loanAmount: ''
  });
  
  // MISMO import state
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoanType, setImportLoanType] = useState('');
  const [importWarnings, setImportWarnings] = useState(null);

  const handleClose = () => {
    // Reset all state
    setStep('choice');
    setFormData({
      loanPurpose: '',
      loanType: '',
      borrowerFirstName: '',
      borrowerLastName: '',
      propertyState: '',
      loanAmount: ''
    });
    setSelectedFile(null);
    setImportLoanType('');
    setError(null);
    setValidationErrors({});
    setImportWarnings(null);
    setLoading(false);
    onClose();
  };

  const handleNativeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});
    
    // Build loan data object
    const loanData = {
      loanPurpose: formData.loanPurpose,
      loanType: formData.loanType,
      loanAmount: parseFloat(formData.loanAmount),
      borrower: {
        firstName: formData.borrowerFirstName,
        lastName: formData.borrowerLastName,
        fullName: `${formData.borrowerFirstName} ${formData.borrowerLastName}`
      },
      property: {
        state: formData.propertyState
      },
      mismo: {
        version: '3.4',
        imported: false
      }
    };
    
    // Validate
    const validation = validateLoanData(loanData);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }
    
    // Create loan
    setLoading(true);
    try {
      const newLoan = await createNewLoan(loanData);
      setLoading(false);
      onLoanCreated(newLoan);
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Failed to create loan');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xml')) {
        setError('Please select an XML file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setImportWarnings(null);
    
    if (!selectedFile) {
      setError('Please select a MISMO XML file');
      return;
    }
    
    if (!importLoanType) {
      setError('Please select a loan type');
      return;
    }
    
    setLoading(true);
    try {
      const result = await importMISMOLoan(selectedFile, importLoanType);
      setLoading(false);
      
      if (result.success) {
        if (result.warnings) {
          setImportWarnings(result.warnings);
        }
        onLoanCreated(result.loan);
        handleClose();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Failed to import MISMO file');
    }
  };

  if (!show) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {step === 'choice' && 'New Loan'}
              {step === 'native' && 'Add New Loan'}
              {step === 'import' && 'Import MISMO 3.4 XML'}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>

          {/* Step 1: Choice */}
          {step === 'choice' && (
            <div className="modal-body">
              <div className="text-secondary mb-4">
                Choose how you want to create a new loan:
              </div>
              
              <div className="row g-3">
                <div className="col-md-6">
                  <div 
                    className="card card-link card-link-pop cursor-pointer"
                    onClick={() => setStep('native')}
                  >
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="ti ti-file-plus" style={{ fontSize: '3rem', color: 'var(--tblr-primary)' }}></i>
                      </div>
                      <h3 className="card-title mb-2">Add New Loan</h3>
                      <p className="text-secondary">
                        Manually enter loan information using our step-based form
                      </p>
                      <div className="mt-3">
                        <span className="badge bg-blue-lt">Native Entry</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div 
                    className="card card-link card-link-pop cursor-pointer"
                    onClick={() => setStep('import')}
                  >
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="ti ti-file-import" style={{ fontSize: '3rem', color: 'var(--tblr-success)' }}></i>
                      </div>
                      <h3 className="card-title mb-2">Import MISMO 3.4 XML</h3>
                      <p className="text-secondary">
                        Upload a MISMO XML file to automatically populate loan data
                      </p>
                      <div className="mt-3">
                        <span className="badge bg-green-lt">XML Import</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Native Form */}
          {step === 'native' && (
            <form onSubmit={handleNativeSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    <i className="ti ti-alert-circle me-2"></i>
                    {error}
                  </div>
                )}
                
                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-info-circle me-2"></i></div>
                    <div>
                      <strong>Draft Mode</strong>
                      <div className="text-secondary small">
                        This loan will be created in Draft state. You can edit it later before submitting to the lender.
                      </div>
                    </div>
                  </div>
                </div>

                <h4 className="mb-3">Loan Information</h4>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">Loan Purpose</label>
                    <select
                      className={`form-select ${validationErrors.loanPurpose ? 'is-invalid' : ''}`}
                      value={formData.loanPurpose}
                      onChange={(e) => setFormData({ ...formData, loanPurpose: e.target.value })}
                      required
                    >
                      <option value="">Select purpose...</option>
                      <option value={LOAN_PURPOSES.PURCHASE}>Purchase</option>
                      <option value={LOAN_PURPOSES.REFINANCE}>Refinance</option>
                    </select>
                    {validationErrors.loanPurpose && (
                      <div className="invalid-feedback">{validationErrors.loanPurpose}</div>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label required">Loan Type</label>
                    <select
                      className={`form-select ${validationErrors.loanType ? 'is-invalid' : ''}`}
                      value={formData.loanType}
                      onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                      required
                    >
                      <option value="">Select type...</option>
                      <option value={LOAN_TYPES.MORTGAGE}>Mortgage</option>
                      <option value={LOAN_TYPES.HELOC}>HELOC</option>
                      <option value={LOAN_TYPES.HOME_EQUITY_LOAN}>Home Equity Loan</option>
                    </select>
                    {validationErrors.loanType && (
                      <div className="invalid-feedback">{validationErrors.loanType}</div>
                    )}
                  </div>
                </div>

                <h4 className="mb-3 mt-4">Borrower Information</h4>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">First Name</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.borrowerFirstName ? 'is-invalid' : ''}`}
                      value={formData.borrowerFirstName}
                      onChange={(e) => setFormData({ ...formData, borrowerFirstName: e.target.value })}
                      required
                    />
                    {validationErrors.borrowerFirstName && (
                      <div className="invalid-feedback">{validationErrors.borrowerFirstName}</div>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label required">Last Name</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.borrowerLastName ? 'is-invalid' : ''}`}
                      value={formData.borrowerLastName}
                      onChange={(e) => setFormData({ ...formData, borrowerLastName: e.target.value })}
                      required
                    />
                    {validationErrors.borrowerLastName && (
                      <div className="invalid-feedback">{validationErrors.borrowerLastName}</div>
                    )}
                  </div>
                </div>

                <h4 className="mb-3 mt-4">Property Information</h4>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">Property State</label>
                    <select
                      className={`form-select ${validationErrors.propertyState ? 'is-invalid' : ''}`}
                      value={formData.propertyState}
                      onChange={(e) => setFormData({ ...formData, propertyState: e.target.value })}
                      required
                    >
                      <option value="">Select state...</option>
                      <option value="CA">California</option>
                      <option value="TX">Texas</option>
                      <option value="FL">Florida</option>
                      <option value="NY">New York</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="IL">Illinois</option>
                      <option value="OH">Ohio</option>
                      <option value="GA">Georgia</option>
                      <option value="NC">North Carolina</option>
                      <option value="MI">Michigan</option>
                    </select>
                    {validationErrors.propertyState && (
                      <div className="invalid-feedback">{validationErrors.propertyState}</div>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label required">Estimated Loan Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className={`form-control ${validationErrors.loanAmount ? 'is-invalid' : ''}`}
                        value={formData.loanAmount}
                        onChange={(e) => setFormData({ ...formData, loanAmount: e.target.value })}
                        min="0"
                        step="1000"
                        required
                      />
                      {validationErrors.loanAmount && (
                        <div className="invalid-feedback">{validationErrors.loanAmount}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary"
                  onClick={() => setStep('choice')}
                  disabled={loading}
                >
                  <i className="ti ti-arrow-left me-1"></i>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check me-1"></i>
                      Save Draft
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: MISMO Import */}
          {step === 'import' && (
            <form onSubmit={handleImportSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    <i className="ti ti-alert-circle me-2"></i>
                    {error}
                  </div>
                )}
                
                {importWarnings && (
                  <div className="alert alert-warning">
                    <i className="ti ti-alert-triangle me-2"></i>
                    {importWarnings}
                  </div>
                )}
                
                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-info-circle me-2"></i></div>
                    <div>
                      <strong>MISMO 3.4 Import</strong>
                      <div className="text-secondary small">
                        Upload a MISMO 3.4 XML file. The XML is the source of truth and will be stored for traceability.
                        The loan will be created in Draft state and can be edited after import.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label required">Select MISMO XML File</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".xml"
                    onChange={handleFileSelect}
                    required
                  />
                  <small className="form-hint">
                    Only MISMO 3.4 XML files are accepted
                  </small>
                  {selectedFile && (
                    <div className="mt-2">
                      <span className="badge bg-blue-lt">
                        <i className="ti ti-file me-1"></i>
                        {selectedFile.name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label required">Loan Type</label>
                  <select
                    className="form-select"
                    value={importLoanType}
                    onChange={(e) => setImportLoanType(e.target.value)}
                    required
                  >
                    <option value="">Select loan type...</option>
                    <option value={LOAN_TYPES.MORTGAGE}>Mortgage</option>
                    <option value={LOAN_TYPES.HELOC}>HELOC</option>
                    <option value={LOAN_TYPES.HOME_EQUITY_LOAN}>Home Equity Loan</option>
                  </select>
                  <small className="form-hint">
                    This cannot be inferred from the XML and must be specified
                  </small>
                </div>

                <div className="alert alert-warning">
                  <strong>Important:</strong> The system will validate the XML structure and flag any missing required fields after import.
                  No data will be silently dropped.
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary"
                  onClick={() => setStep('choice')}
                  disabled={loading}
                >
                  <i className="ti ti-arrow-left me-1"></i>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading || !selectedFile || !importLoanType}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-file-import me-1"></i>
                      Import & Create Draft
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewLoanModal;
